// Panel registry — the one place that knows which panels exist, how wide they
// are, and where the row that holds them sits.
//
// Widths are in dvw and are the numbers each panel was already built around
// (App.css #chord-container / .emo-display / .pie-chart, and the StaffView
// width prop in App.tsx). A panel keeps its exact width when it moves, so its
// interior never has to be re-tuned — that is what makes rearranging cheap.
//
// Everything here is in dvw, which tracks viewport WIDTH, so the row's budget
// is a constant. No measuring, no ResizeObserver.
//
export type PanelId = 'chordSymbol' | 'staff' | 'sentiment' | 'chordCompass' | 'luxTonnetz' | 'chordChart';

export interface PanelDef {
  id: PanelId;
  title: string;
  /** Outer width in dvw. Every element is border-box (App.css:50). */
  width: number;
  /** Narrowest the panel could render if it ever has to shrink. Unused today. */
  minWidth: number;
  /**
   * dvw this panel's top sits below the row's, leaving a strip above it for
   * controls that belong to the panel but sit outside it. The panel loses the
   * same amount of height, so its bottom edge still lines up with the rest.
   */
  topInset?: number;
}

// The row starts one PANEL_GAP clear of the Key-Center column, which ends at
// 15.6dvw, and stops one PANEL_GAP short of the window edge — so with a full
// row the spacing reads the same all the way across: beside the Key-Center,
// between each pair of panels, and after the last one.

/** Left edge of the row. The Key-Center column is pinned to its left. */
export const ROW_LEFT = 16.6;

/** Right edge of the row. */
export const ROW_RIGHT = 99;

/** Top edge, shared by every panel. */
export const ROW_TOP = 3;

/** Space between two neighbouring panels, and the slot the move arrows sit in. */
export const PANEL_GAP = 1.0;

/** Every panel is the same height. */
export const PANEL_HEIGHT = 31.5;

/** Total width available to panels and the gaps between them. */
export const ROW_BUDGET = ROW_RIGHT - ROW_LEFT;

export const PANELS: Record<PanelId, PanelDef> = {
  // 20, not the 20.5 the inline style asked for: #chord-container also carried
  // max-width: 20dvw, which quietly clamped it.
  chordSymbol: { id: 'chordSymbol', title: 'Chord Symbol', width: 20, minWidth: 20 },
  staff: { id: 'staff', title: 'Staff', width: 22.4, minWidth: 22.4 },
  sentiment: { id: 'sentiment', title: 'Sentiment', width: 22, minWidth: 22 },
  chordCompass: { id: 'chordCompass', title: 'Chord Compass', width: 36, minWidth: 28 },
  luxTonnetz: { id: 'luxTonnetz', title: 'Lux Tonnetz', width: 36, minWidth: 28 },
  // The chart's Export / navigation / edit buttons live in the strip above it.
  chordChart: { id: 'chordChart', title: 'Chord Chart', width: 36, minWidth: 28, topInset: 2.5 },
};

/**
 * Left to right. The Lux Tonnetz starts hidden, but holds the first slot so
 * that showing it puts it beside the Key-Center column, ahead of every other
 * panel. The chart sits ahead of the Compass so that, when it opens itself on
 * the first chord, it lands beside the Staff and the Compass is what waits off
 * the right of the view.
 */
export const DEFAULT_ORDER: PanelId[] = ['luxTonnetz', 'chordSymbol', 'staff', 'chordChart', 'sentiment', 'chordCompass'];

/** Width a set of panels occupies, including the gaps between them. */
export function rowWidth(ids: PanelId[]): number {
  if (ids.length === 0) return 0;
  let total = 0;
  for (let i = 0; i < ids.length; i++) {
    total += PANELS[ids[i]].width;
  }
  return total + PANEL_GAP * (ids.length - 1);
}

// Widths like 22.4 and 1.45 are not exactly representable in binary floating
// point, so a row that fills the budget precisely — as the three default panels
// do — lands a hair OVER it: 81.30000000000001 against 81.3. Without slack a
// row that exactly fills would drop its last panel out of the view. A thousandth of a dvw is
// about a fiftieth of a pixel, far below anything that could overlap.
const FIT_EPSILON = 0.001;

// The panel stack.
//
// Every panel that is switched on sits in a stack, in `order`, left to right.
// The row shows a window onto it — the view — anchored at the row's left edge:
// it starts at one panel and takes as many of the panels after it as fit.
// Scrolling moves the start. A panel that does not fit is never refused or
// displaced; it is further along the stack than the view reaches. The stack
// has ends: nothing goes left of the first panel or right of the last.
//
// Panels can be locked on the left, one after another, like the Key-Center
// column. A locked panel leaves the stack and stays put; the stack scrolls in
// the room to its right. Locks come off in reverse order, innermost first.

/**
 * The arrangement the app keeps: the order panels sit in, the ones switched
 * off, the ones locked on the left, and the panel the view starts at. It is
 * one plain object so it can be saved as is, and a saved layout is the same
 * shape with a name.
 */
export interface PanelLayout {
  order: PanelId[];
  hidden: PanelId[];
  /** Locked on the left, left to right. Each keeps its slot in `order`, unused while locked. */
  locked: PanelId[];
  /** A panel in the stack, unless the stack is empty — hidePanel and lockPanel hand it on. */
  start: PanelId;
}

/**
 * Whether a panel is in the stack: switched on and not locked. `also` counts
 * one more panel in even if it is off, for the slot it would return to.
 */
const inStack = (layout: PanelLayout, also?: PanelId) => (p: PanelId) =>
  (p === also || layout.hidden.indexOf(p) === -1) && layout.locked.indexOf(p) === -1;

/** The panels switched on and not locked, left to right: what scrolls. */
export function panelStack(layout: PanelLayout): PanelId[] {
  return layout.order.filter(inStack(layout));
}

/** Where the scrolling region starts: past the locked panels and one gap. */
function regionLeft(locked: PanelId[]): number {
  return locked.length === 0 ? 0 : rowWidth(locked) + PANEL_GAP;
}

/** Whether these panels fit in the scrolling region beside these locked ones. */
function fitsRegion(ids: PanelId[], locked: PanelId[]): boolean {
  return rowWidth(ids) <= ROW_BUDGET - regionLeft(locked) + FIT_EPSILON;
}

/** From `start`, as many panels as fit. At least one while any panel fits the region at all. */
function viewFrom(stack: PanelId[], start: number, locked: PanelId[]): PanelId[] {
  const view: PanelId[] = [];
  for (let i = start; i < stack.length; i++) {
    if (!fitsRegion(view.concat([stack[i]]), locked)) {
      break;
    }
    view.push(stack[i]);
  }
  return view;
}

/**
 * Where the view starts. The start is pulled back while the panel before it
 * fits in the room the view leaves on its right, so the view never leaves
 * space empty that a panel waiting off its left could fill — closing a panel,
 * scrolling to the end or pushing a wide panel out for a narrow one all fill
 * the view back up from the left once nothing more fits from the right.
 */
function stackStartIndex(layout: PanelLayout, stack: PanelId[]): number {
  let s = Math.max(0, stack.indexOf(layout.start));
  while (s > 0) {
    const end = s + viewFrom(stack, s, layout.locked).length;
    if (!fitsRegion(stack.slice(s - 1, end), layout.locked)) {
      break;
    }
    s--;
  }
  return s;
}

/** The panels on screen, left to right: the locked ones, then the view. */
export function stackView(layout: PanelLayout): PanelId[] {
  const stack = panelStack(layout);
  return layout.locked.concat(viewFrom(stack, stackStartIndex(layout, stack), layout.locked));
}

/** The start that brings `id` into view by the shortest scroll, or `start` when it is already there. */
function scrollToShow(stack: PanelId[], start: number, id: PanelId, locked: PanelId[]): number {
  const at = stack.indexOf(id);
  if (at === -1) return start;
  if (at < start) return at;
  let s = start;
  while (viewFrom(stack, s, locked).indexOf(id) === -1) {
    s++;
  }
  return s;
}

/** The start handed on when `id` leaves the stack: the panel after it, or before it at the end. */
function startWithout(layout: PanelLayout, id: PanelId): PanelId {
  if (layout.start !== id) return layout.start;
  const stack = panelStack(layout);
  const at = stack.indexOf(id);
  if (at + 1 < stack.length) return stack[at + 1];
  if (at > 0) return stack[at - 1];
  return layout.start;
}

/** Switch a panel off. A locked panel is unlocked as it goes. */
export function hidePanel(layout: PanelLayout, id: PanelId): PanelLayout {
  if (layout.hidden.indexOf(id) !== -1) return layout;
  return {
    order: layout.order,
    hidden: layout.hidden.concat([id]),
    locked: layout.locked.filter(p => p !== id),
    start: startWithout(layout, id),
  };
}

/**
 * Switch a panel on, back in its slot, and scroll it into view. Also brings a
 * panel that is already on into view, and returns the same layout when there
 * is nothing to do.
 */
export function showPanel(layout: PanelLayout, id: PanelId): PanelLayout {
  if (layout.locked.indexOf(id) !== -1) return layout;
  const hidden = layout.hidden.filter(p => p !== id);
  const shown = { order: layout.order, hidden: hidden, locked: layout.locked, start: layout.start };
  const stack = panelStack(shown);
  if (stack.indexOf(layout.start) === -1) {
    return { order: layout.order, hidden: hidden, locked: layout.locked, start: id };
  }
  const s = stackStartIndex(shown, stack);
  const t = scrollToShow(stack, s, id, layout.locked);
  if (hidden.length === layout.hidden.length && t === s) {
    return layout;
  }
  return { order: layout.order, hidden: hidden, locked: layout.locked, start: stack[t] };
}

/**
 * Whether `id` can be locked: it has to be the leftmost panel of the view —
 * the one against the locked panels or the Key-Center column — and every panel
 * left unlocked, switched on or not, must still fit in the room that leaves.
 * Otherwise a wide panel could never be shown again.
 */
export function canLockPanel(layout: PanelLayout, id: PanelId): boolean {
  const stack = panelStack(layout);
  if (viewFrom(stack, stackStartIndex(layout, stack), layout.locked)[0] !== id) {
    return false;
  }
  return locksLeaveRoom(layout.order, layout.locked.concat([id]));
}

/** Whether every panel not in `locked` still fits on its own in the room these locks leave. */
export function locksLeaveRoom(order: PanelId[], locked: PanelId[]): boolean {
  return order.filter(p => locked.indexOf(p) === -1).every(p => fitsRegion([p], locked));
}

/** Lock the leftmost panel of the view. Nothing on screen moves; the stack now scrolls to its right. */
export function lockPanel(layout: PanelLayout, id: PanelId): PanelLayout {
  if (!canLockPanel(layout, id)) return layout;
  return {
    order: layout.order,
    hidden: layout.hidden,
    locked: layout.locked.concat([id]),
    start: startWithout(layout, id),
  };
}

/** Whether `id` is the innermost locked panel — the only one that can be unlocked. */
export function canUnlockPanel(layout: PanelLayout, id: PanelId): boolean {
  return layout.locked.length > 0 && layout.locked[layout.locked.length - 1] === id;
}

/**
 * Unlock the innermost locked panel, back into the stack just ahead of the
 * view, where it already is on screen: the view starts at it, so nothing
 * moves, and it now scrolls with the rest.
 */
export function unlockPanel(layout: PanelLayout, id: PanelId): PanelLayout {
  if (!canUnlockPanel(layout, id)) return layout;
  const stack = panelStack(layout);
  const order = layout.order.filter(p => p !== id);
  if (stack.length > 0) {
    const first = stack[stackStartIndex(layout, stack)];
    order.splice(order.indexOf(first), 0, id);
  } else {
    order.push(id);
  }
  return { order: order, hidden: layout.hidden, locked: layout.locked.slice(0, -1), start: id };
}

/**
 * Move a panel one place along the stack, swapping it with its neighbour.
 * The first panel cannot move left, nor the last right, and locked panels do
 * not move at all.
 *
 * The view stays on the same positions unless the panel moved would leave it:
 * a panel pushed past the edge of the view takes the view with it, scrolled
 * as little as brings it back in (showPanel).
 */
export function stepPanel(layout: PanelLayout, id: PanelId, direction: number): PanelLayout {
  const stack = panelStack(layout);
  const s = stackStartIndex(layout, stack);
  const at = stack.indexOf(id);
  const target = at + direction;
  if (at === -1 || target < 0 || target >= stack.length) {
    return layout;
  }
  const neighbour = stack[target];
  const from = layout.order.indexOf(id);
  const to = layout.order.indexOf(neighbour);
  const order = layout.order.slice();
  order[from] = neighbour;
  order[to] = id;
  const swapped = order.filter(inStack(layout));
  return showPanel({ order: order, hidden: layout.hidden, locked: layout.locked, start: swapped[s] }, id);
}

/**
 * Scroll the view until the next panel beyond one edge is in it — what the
 * scroll controls under the row do. The two directions mirror each other:
 * the view gives up as few panels as it can on the far side to fit the panel
 * coming in, then fills from the near side with as many more as fit. Right
 * moves the start forward until the panel after the view fits; left pulls the
 * end back until the panel before it fits, then takes in every panel before
 * that which also fits.
 */
export function scrollStack(layout: PanelLayout, direction: number): PanelLayout {
  const stack = panelStack(layout);
  const s = stackStartIndex(layout, stack);
  if (direction < 0) {
    if (s === 0) return layout;
    let end = s - 1;
    while (end + 1 < stack.length && fitsRegion(stack.slice(s - 1, end + 2), layout.locked)) {
      end++;
    }
    let t = s - 1;
    while (t > 0 && fitsRegion(stack.slice(t - 1, end + 1), layout.locked)) {
      t--;
    }
    return { order: layout.order, hidden: layout.hidden, locked: layout.locked, start: stack[t] };
  }
  const end = s + viewFrom(stack, s, layout.locked).length;
  if (end >= stack.length) {
    return layout;
  }
  const t = scrollToShow(stack, s, stack[end], layout.locked);
  return { order: layout.order, hidden: layout.hidden, locked: layout.locked, start: stack[t] };
}

/**
 * The lineup Settings' meter draws: the locked panels, then the stack, with
 * `id` in the slot it would return to if it is off.
 */
export function meterLineup(layout: PanelLayout, id: PanelId): PanelId[] {
  return layout.locked.concat(layout.order.filter(inStack(layout, id)));
}

/**
 * Move a panel to a place in the meter's lineup, the way dropping it there
 * would. Counting along the same lineup the meter draws means a block's
 * position and its index here cannot drift apart. Locked panels neither move
 * nor give up their places: a drop on one lands just past them. Panels that
 * are off keep their slots in `order`. The view keeps its start.
 */
export function placePanel(layout: PanelLayout, id: PanelId, index: number): PanelLayout {
  if (layout.locked.indexOf(id) !== -1) return layout;
  const inLineup = inStack(layout, id);
  const lineup = layout.order.filter(inLineup);
  const at = lineup.indexOf(id);
  const target = Math.max(0, index - layout.locked.length);
  if (at === -1 || target >= lineup.length || target === at) {
    return layout;
  }
  const moved = lineup.slice();
  moved.splice(at, 1);
  moved.splice(target, 0, id);

  // Write the new sequence back into the slots the lineup occupied.
  const order = layout.order.slice();
  let next = 0;
  for (let i = 0; i < order.length; i++) {
    if (inLineup(layout.order[i])) {
      order[i] = moved[next];
      next++;
    }
  }
  // A start that was the panel moved hands on to whatever now holds its place.
  const start = layout.start === id ? moved[Math.min(at, moved.length - 1)] : layout.start;
  return { order: order, hidden: layout.hidden, locked: layout.locked, start: start };
}

/**
 * The row laid out: each on-screen panel's left edge in dvw from the row's
 * left edge — the locked panels, then the view packed after them — where the
 * scrolling region starts, and for every panel in the stack but out of view,
 * the side of the view it is waiting on, which is the side it leaves by or
 * arrives from.
 */
export function stackPositions(layout: PanelLayout): {
  view: PanelId[];
  x: Partial<Record<PanelId, number>>;
  side: Partial<Record<PanelId, 'left' | 'right'>>;
  regionLeft: number;
} {
  const stack = panelStack(layout);
  const s = stackStartIndex(layout, stack);
  const scrolling = viewFrom(stack, s, layout.locked);
  const view = layout.locked.concat(scrolling);
  const x: Partial<Record<PanelId, number>> = {};
  const side: Partial<Record<PanelId, 'left' | 'right'>> = {};
  let cursor = 0;
  for (let i = 0; i < view.length; i++) {
    x[view[i]] = cursor;
    cursor += PANELS[view[i]].width + PANEL_GAP;
  }
  for (let i = 0; i < stack.length; i++) {
    if (i < s) side[stack[i]] = 'left';
    if (i >= s + scrolling.length) side[stack[i]] = 'right';
  }
  return { view: view, x: x, side: side, regionLeft: regionLeft(layout.locked) };
}
